// src/components/modals/QRScannerModal.jsx

import React, { useState } from 'react';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import SimpleQRScanner from '../common/SimpleQRScanner';
import { qrValidation } from '../../helpers/tpvHelper';
import { qrErrorHandler } from '../../helpers/qrErrorHandler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const QRScannerModal = ({ isOpen, onClose, onQRScanned }) => {
    const [loading, setLoading] = useState(false);

    const handleQRScanned = async (qrData) => {
        try {
            setLoading(true);

            // Validar datos del QR (soporte para URLs y JSON)
            let validation;
            if (qrData.source === 'url' && qrData.ticketId) {
                // Para URLs de tickets, validar que tenga ticketId
                validation = { valid: true, data: qrData };
            } else {
                // Para JSON, usar la validación existente
                validation = qrValidation.validateQRData(JSON.stringify(qrData));
            }

            if (!validation.valid) {
                await qrErrorHandler.handleValidationError(validation.error);
                return;
            }

            // Obtener datos del ticket y cliente desde el backend
            const ticketData = await get(`tickets/${qrData.ticketId}`);

            if (!ticketData) {
                await qrErrorHandler.handleBackendError({
                    status: 404,
                    message: 'No se pudo obtener la información del ticket'
                });
                return;
            }

            // Cerrar el modal de escaneo
            onClose();

            // Llamar al callback con los datos del ticket para abrir el modal de acumulación
            if (onQRScanned) {
                onQRScanned({
                    ticket: ticketData,
                    qrData: qrData
                });
            }

        } catch (error) {
            console.error('Error processing QR:', error);
            await qrErrorHandler.handleBackendError(error);
        } finally {
            setLoading(false);
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-2 sm:p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2 sm:mx-4">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faQrcode} className="text-white text-lg sm:text-xl" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg sm:text-xl font-bold text-white truncate">Escanear QR</h2>
                            <p className="text-xs sm:text-sm text-white/80 truncate">Acumular puntos desde ticket del cliente</p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            // Close modal - page will reload when scanner stops
                            console.log('Modal closing');
                            onClose();
                        }}
                        className="text-white hover:text-gray-200 transition-colors p-1 sm:p-2 flex-shrink-0"
                    >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-120px)]">
                    {loading ? (
                        <div className="text-center py-6 sm:py-8">
                            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 text-sm sm:text-base">Procesando QR...</p>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div className="mb-4 sm:mb-6">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                    <FontAwesomeIcon icon={faQrcode} className="text-purple-600 text-2xl sm:text-3xl" />
                                </div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Escáner de QR</h3>
                                <p className="text-sm sm:text-base text-gray-600 px-2">
                                    Escanea el código QR del ticket del cliente para acumular puntos
                                </p>
                            </div>

                            <div className="bg-white rounded-lg shadow-md p-2 sm:p-4">
                                <SimpleQRScanner
                                    onQRScanned={handleQRScanned}
                                    onClose={onClose}
                                />
                            </div>

                            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
                                <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Instrucciones:</h4>
                                <ul className="text-xs sm:text-sm text-blue-800 space-y-1 text-left">
                                    <li>• Pide al cliente que muestre su ticket con QR</li>
                                    <li>• Apunta la cámara hacia el código QR</li>
                                    <li>• Ingresa el monto y puntos a acumular</li>
                                    <li>• Confirma la transacción</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QRScannerModal;
